import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Permission, Role, hasPermission, hasAnyPermission, hasAllPermissions } from '../services/permissions';
import { api } from '../services/api';

/**
 * Hook pour vérifier les permissions de l'utilisateur connecté
 * Prend en compte les permissions personnalisées des collaborateurs
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const [collaboratorPermissions, setCollaboratorPermissions] = useState<string[]>([]);
  const [collaboratorSections, setCollaboratorSections] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  
  const role: Role = (user?.role as Role) || 'agent_terrain';
  
  useEffect(() => {
    const loadCollaboratorData = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      
      try {
        const { collaborator } = await api.collaborators.getByUserId(user.id);
        if (collaborator && collaborator.is_active) {
          setCollaboratorPermissions(collaborator.permissions || []);
          setCollaboratorSections(collaborator.sections_access || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données collaborateur:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCollaboratorData();
  }, [user?.id]);
  
  // Vérifie si l'utilisateur a une permission (collaborateur ou rôle principal)
  const checkPermission = (permission: Permission): boolean => {
    // Si l'utilisateur est collaborateur avec permissions personnalisées
    if (collaboratorPermissions.length > 0) {
      return collaboratorPermissions.includes(permission);
    }
    // Sinon, utiliser les permissions du rôle principal
    return hasPermission(role, permission);
  };
  
  // Vérifie si l'utilisateur a accès à une section
  const hasSectionAccess = (sectionId: string): boolean => {
    // Si l'utilisateur est collaborateur avec sections personnalisées
    if (collaboratorSections.length > 0) {
      return collaboratorSections.includes(sectionId);
    }
    // Pour les rôles normaux, vérifier les permissions de base selon la section
    // Mapping des sections vers les permissions requises
    const sectionPermissions: Record<string, Permission[]> = {
      'dashboard': [], // Tous ont accès
      'projects': ['projects:view_assigned', 'projects:view_all'],
      'reporting': ['reports:create', 'reports:view_all'],
      'data_collection': ['surveys:respond', 'surveys:create'],
      'communication': [], // Tous ont accès
      'inventory': ['inventory:scan', 'inventory:manage'],
      'knowledge': ['knowledge:view_essential'],
      'kpis': ['kpis:view_own', 'kpis:view_all'],
      'users': [], // Seulement admin/agence
    };
    
    const requiredPermissions = sectionPermissions[sectionId] || [];
    if (requiredPermissions.length === 0) {
      // Pas de permission requise ou section admin
      if (sectionId === 'users') {
        return role === 'admin' || role === 'agence';
      }
      return true;
    }
    
    // Vérifier si l'utilisateur a au moins une des permissions requises
    return requiredPermissions.some(perm => checkPermission(perm));
  };
  
  return {
    role,
    hasPermission: checkPermission,
    hasAnyPermission: (permissions: Permission[]) => permissions.some(p => checkPermission(p)),
    hasAllPermissions: (permissions: Permission[]) => permissions.every(p => checkPermission(p)),
    hasSectionAccess,
    isAgentTerrain: role === 'agent_terrain',
    isAgence: role === 'agence',
    isSOBEBRA: role === 'soebra',
    isAdmin: role === 'admin',
    isCollaborator: collaboratorPermissions.length > 0,
    loading,
  };
};



